import type { ReactNode } from 'react'

export type Tone = 'neutral' | 'accent' | 'ok' | 'warn' | 'crit'
export type MetricSize = 'xl' | 'lg' | 'base' | 'sm'

const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-fg',
  accent: 'text-accent',
  ok: 'text-ok',
  warn: 'text-warn',
  crit: 'text-crit',
}

const SIZE: Record<MetricSize, string> = {
  xl: 'text-xl',
  lg: 'text-lg',
  base: 'text-base',
  sm: 'text-sm',
}

interface MetricProps {
  label: string
  value: string
  /** Rendered smaller and muted next to the value: "ev/s", "ms", "%". */
  unit?: string
  size?: MetricSize
  tone?: Tone
  /** Second line under the value — a comparison, a denominator, a hint. */
  note?: ReactNode
  /** Puts the label under the value. Default is above. */
  labelBelow?: boolean
  className?: string
}

/**
 * A labelled live number.
 *
 * Every value carries `.live-value`, which does two things: tabular figures so
 * digits never change width between ticks, and the freeze treatment driven by
 * `[data-connection]` on <html>. Nothing here animates — a rolling counter on a
 * number that changes every 2s is noise, not feedback.
 */
export function Metric({
  label,
  value,
  unit,
  size = 'base',
  tone = 'neutral',
  note,
  labelBelow = false,
  className = '',
}: MetricProps) {
  const labelEl = (
    <span className="block truncate text-micro uppercase tracking-label text-fg-muted">
      {label}
    </span>
  )

  return (
    <div className={`min-w-0 ${className}`}>
      {!labelBelow && labelEl}
      <div className={`flex items-baseline gap-1 ${SIZE[size]} ${TONE_TEXT[tone]}`}>
        <span className="live-value font-mono leading-none">{value}</span>
        {unit !== undefined && (
          <span className="text-micro font-normal text-fg-muted">{unit}</span>
        )}
      </div>
      {labelBelow && <div className="mt-0.5">{labelEl}</div>}
      {note !== undefined && <div className="mt-0.5 text-micro text-fg-muted">{note}</div>}
    </div>
  )
}

/** Inline monospace figure for use inside prose or a table cell. */
export function Figure({
  children,
  tone = 'neutral',
  className = '',
}: {
  children: ReactNode
  tone?: Tone
  className?: string
}) {
  return (
    <span className={`live-value font-mono ${TONE_TEXT[tone]} ${className}`}>{children}</span>
  )
}
